/**
 * ALOHA QUEEN SUPERNOVA - Comprehensive Admin Settings
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * FULL ADMIN CONTROL PANEL
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Settings, Crown, CreditCard, Mic, Palette, Database, 
  Users, Shield, Zap, Globe, Save, RefreshCw, Trash2,
  Eye, EyeOff, Key, Bell, Mail
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComprehensiveAdminSettings = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // General Settings
    platformName: 'Aloha Queen Supernova',
    maintenanceMode: false,
    signupsEnabled: true,
    
    // Payment Settings
    stripePublishableKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    stripeLiveMode: true,
    allowTrials: true,
    
    // AI Agent Settings
    defaultModel: 'gemini-2.0-flash',
    enableGPTRealtime: true,
    enableVoiceSynthesis: true,
    enableDeepSearch: true,
    maxPromptsAnonymous: 3,
    maxPromptsFree: 10,
    
    // Voice Settings
    azureSpeechEnabled: true,
    elevenLabsEnabled: true,
    defaultVoiceProvider: 'azure',
    
    // 3D Avatar Settings
    avatarsEnabled: true,
    queenSupernovaAvailable: true,
    kingNovaAvailable: true,
    kingMilkmanExclusive: true,
    
    // Background Settings
    backgroundType: 'enhanced8k', // 'enhanced8k', 'realistic', 'simple', 'custom'
    backgroundQuality: 'high', // 'low', 'medium', 'high', 'ultra'
    backgroundColor: '#0F172A',
    show3DEffects: true,
    showAnimations: true,
    
    // Security Settings
    require2FA: false,
    sessionTimeout: 7, // days
    maxLoginAttempts: 5,
    
    // Admin Users
    adminEmails: ['malcolmlee3@gmail.com'],
    
    // Cache Settings
    cacheEnabled: true,
    cacheExpiry: 24 // hours
  });
  
  const [adminUsers, setAdminUsers] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');

  useEffect(() => {
    if (!user || user.subscription_tier !== 'LIFETIME_ENTERPRISE') {
      navigate('/');
      return;
    }
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    // Load current settings from backend or localStorage
    const savedSettings = localStorage.getItem('adminSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
    
    // Load admin users list
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const admins = response.data.users.filter(u => 
        u.subscription_tier === 'LIFETIME_ENTERPRISE' || u.is_admin
      );
      setAdminUsers(admins);
    } catch (error) {
      console.error('Failed to load admin users:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Save to localStorage
      localStorage.setItem('adminSettings', JSON.stringify(settings));
      
      // TODO: Save to backend when endpoint is ready
      // await axios.post(`${API}/admin/settings`, settings, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });
      
      alert('✅ Settings saved successfully!');
    } catch (error) {
      alert('❌ Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const clearCache = async () => {
    if (!window.confirm('Clear all cached data? This will log out all users.')) return;
    
    try {
      // Clear localStorage
      const adminSettings = localStorage.getItem('adminSettings');
      localStorage.clear();
      if (adminSettings) localStorage.setItem('adminSettings', adminSettings);
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      alert('✅ Cache cleared successfully!');
    } catch (error) {
      alert('❌ Error clearing cache');
    }
  };

  const clearOldData = async () => {
    if (!window.confirm('Delete old anonymous sessions and expired data?')) return;
    
    try {
      await axios.post(`${API}/admin/cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Old data cleaned up!');
    } catch (error) {
      alert('❌ Error cleaning data');
    }
  };

  const addAdmin = async () => {
    if (!newAdminEmail || !newAdminEmail.includes('@')) {
      alert('Enter a valid email');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/add-admin`, 
        { email: newAdminEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`✅ Admin access granted to ${newAdminEmail}`);
      setNewAdminEmail('');
      loadSettings();
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.detail || 'Failed to add admin'}`);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'ai', label: 'AI & Voice', icon: Mic },
    { id: 'avatars', label: '3D Avatars', icon: Users },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'admins', label: 'Admin Users', icon: Crown },
    { id: 'maintenance', label: 'Maintenance', icon: Database }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Crown className="w-10 h-10 text-yellow-400" />
                <h1 className="text-4xl font-bold">Admin Control Panel</h1>
              </div>
              <p className="text-gray-400">Complete platform configuration and management</p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/admin/test-users"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition"
              >
                Test Users
              </Link>
              <Link
                to="/admin"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition"
              >
                Dashboard
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Settings Content */}
        <div className="bg-gray-800 rounded-xl p-6">
          
          {/* GENERAL TAB */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">General Settings</h2>
              
              <div>
                <label className="block text-gray-300 mb-2">Platform Name</label>
                <input
                  type="text"
                  value={settings.platformName}
                  onChange={(e) => setSettings({...settings, platformName: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">Maintenance Mode</p>
                  <p className="text-sm text-gray-400">Disable access for all users except admins</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, maintenanceMode: !settings.maintenanceMode})}
                  className={`w-16 h-8 rounded-full transition ${settings.maintenanceMode ? 'bg-red-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.maintenanceMode ? 'translate-x-8' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">New Signups Enabled</p>
                  <p className="text-sm text-gray-400">Allow new user registrations</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, signupsEnabled: !settings.signupsEnabled})}
                  className={`w-16 h-8 rounded-full transition ${settings.signupsEnabled ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.signupsEnabled ? 'translate-x-8' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          )}

          {/* PAYMENTS TAB */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">Payment Settings</h2>
              
              <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-green-400 text-2xl">✓</span>
                  <p className="font-bold text-green-300">Stripe Live Mode: ENABLED</p>
                </div>
                <p className="text-sm text-gray-300">Account: malcolmlee3@gmail.com</p>
                <p className="text-sm text-gray-300">Payments go directly to your bank account</p>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Stripe Publishable Key</label>
                <div className="relative">
                  <input
                    type="text"
                    value={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_...'}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    disabled
                  />
                  <Key className="absolute right-3 top-3 w-5 h-5 text-gray-500" />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Stripe Webhook Status</label>
                <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 font-semibold">✅ Configured & Active</p>
                  <p className="text-sm text-gray-400 mt-1">Webhook endpoint: /api/webhooks/stripe</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">Allow Trial Periods</p>
                  <p className="text-sm text-gray-400">Enable free trial for new subscribers</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, allowTrials: !settings.allowTrials})}
                  className={`w-16 h-8 rounded-full transition ${settings.allowTrials ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.allowTrials ? 'translate-x-8' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div>
                <h3 className="font-bold mb-3">Subscription Tiers</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { name: 'BASIC', price: '$9.99', color: 'blue' },
                    { name: 'PRO', price: '$49.99', color: 'purple' },
                    { name: 'ENTERPRISE', price: '$99.99', color: 'yellow' }
                  ].map(tier => (
                    <div key={tier.name} className={`p-4 bg-${tier.color}-900/20 border border-${tier.color}-500/30 rounded-lg`}>
                      <p className="font-bold text-white">{tier.name}</p>
                      <p className={`text-${tier.color}-400 text-xl font-bold`}>{tier.price}/mo</p>
                      <p className="text-xs text-gray-400 mt-2">Active & Configured</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* AI & VOICE TAB */}
          {activeTab === 'ai' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">AI Agent & Voice Settings</h2>
              
              <div>
                <label className="block text-gray-300 mb-2">Default AI Model</label>
                <select
                  value={settings.defaultModel}
                  onChange={(e) => setSettings({...settings, defaultModel: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash (Current)</option>
                  <option value="gpt-5">GPT-5</option>
                  <option value="claude-4-sonnet">Claude 4 Sonnet</option>
                  <option value="gpt-4o">GPT-4o</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">GPT Realtime (Speech-to-Speech)</p>
                  <p className="text-sm text-gray-400">Enable OpenAI Realtime API</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-400 text-sm font-semibold">ACTIVE</span>
                  <button
                    onClick={() => setSettings({...settings, enableGPTRealtime: !settings.enableGPTRealtime})}
                    className={`w-16 h-8 rounded-full transition ${settings.enableGPTRealtime ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.enableGPTRealtime ? 'translate-x-8' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">Voice Synthesis</p>
                  <p className="text-sm text-gray-400">Enable AI voice generation</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-400 text-sm font-semibold">ENABLED</span>
                  <button
                    onClick={() => setSettings({...settings, enableVoiceSynthesis: !settings.enableVoiceSynthesis})}
                    className={`w-16 h-8 rounded-full transition ${settings.enableVoiceSynthesis ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.enableVoiceSynthesis ? 'translate-x-8' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">Deep Web Search</p>
                  <p className="text-sm text-gray-400">Enable research capabilities</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-400 text-sm font-semibold">OPERATIONAL</span>
                  <button
                    onClick={() => setSettings({...settings, enableDeepSearch: !settings.enableDeepSearch})}
                    className={`w-16 h-8 rounded-full transition ${settings.enableDeepSearch ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.enableDeepSearch ? 'translate-x-8' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Voice Provider Priority</label>
                <select
                  value={settings.defaultVoiceProvider}
                  onChange={(e) => setSettings({...settings, defaultVoiceProvider: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="azure">Azure Speech (Primary)</option>
                  <option value="elevenlabs">ElevenLabs</option>
                  <option value="openai">OpenAI TTS</option>
                  <option value="gpt-realtime">GPT Realtime</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2">Anonymous User Limit</label>
                  <input
                    type="number"
                    value={settings.maxPromptsAnonymous}
                    onChange={(e) => setSettings({...settings, maxPromptsAnonymous: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Free User Limit</label>
                  <input
                    type="number"
                    value={settings.maxPromptsFree}
                    onChange={(e) => setSettings({...settings, maxPromptsFree: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 3D AVATARS TAB */}
          {activeTab === 'avatars' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">3D Avatar Configuration</h2>
              
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">3D Avatars Enabled</p>
                  <p className="text-sm text-gray-400">Enable 3D avatar rendering</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-400 text-sm font-semibold">AVAILABLE</span>
                  <button
                    onClick={() => setSettings({...settings, avatarsEnabled: !settings.avatarsEnabled})}
                    className={`w-16 h-8 rounded-full transition ${settings.avatarsEnabled ? 'bg-green-500' : 'bg-gray-600'}`}
                  >
                    <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.avatarsEnabled ? 'translate-x-8' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-bold text-lg">Avatar Availability</h3>
                
                {[
                  { id: 'queenSupernovaAvailable', name: 'Queen Supernova', desc: 'Beautiful female Hawaiian avatar', users: 'All users' },
                  { id: 'kingNovaAvailable', name: 'King Nova', desc: 'Strong male Hawaiian avatar', users: 'All users' },
                  { id: 'kingMilkmanExclusive', name: 'King Milkman', desc: 'Creator avatar (EXCLUSIVE)', users: 'malcolmlee3@gmail.com only' }
                ].map(avatar => (
                  <div key={avatar.id} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                    <div>
                      <p className="font-semibold">{avatar.name}</p>
                      <p className="text-sm text-gray-400">{avatar.desc}</p>
                      <p className="text-xs text-purple-400 mt-1">{avatar.users}</p>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, [avatar.id]: !settings[avatar.id]})}
                      className={`w-16 h-8 rounded-full transition ${settings[avatar.id] ? 'bg-green-500' : 'bg-gray-600'}`}
                    >
                      <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings[avatar.id] ? 'translate-x-8' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* APPEARANCE TAB */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">Appearance & Background Settings</h2>
              
              <div>
                <label className="block text-gray-300 mb-2">Background Type</label>
                <select
                  value={settings.backgroundType}
                  onChange={(e) => setSettings({...settings, backgroundType: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="enhanced8k">Enhanced 8K Galaxy (Current)</option>
                  <option value="realistic">Realistic Galaxy</option>
                  <option value="simple">Simple Space</option>
                  <option value="custom">Custom Background</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Graphics Quality</label>
                <select
                  value={settings.backgroundQuality}
                  onChange={(e) => setSettings({...settings, backgroundQuality: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="low">Low (2K) - Best Performance</option>
                  <option value="medium">Medium (4K)</option>
                  <option value="high">High (6K) - Recommended</option>
                  <option value="ultra">Ultra (8K) - Best Quality</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Background Base Color</label>
                <div className="flex gap-3">
                  <input
                    type="color"
                    value={settings.backgroundColor}
                    onChange={(e) => setSettings({...settings, backgroundColor: e.target.value})}
                    className="w-20 h-12 bg-gray-700 border border-gray-600 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.backgroundColor}
                    onChange={(e) => setSettings({...settings, backgroundColor: e.target.value})}
                    className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="#0F172A"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">3D Effects</p>
                  <p className="text-sm text-gray-400">Enable 3D rendering effects</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, show3DEffects: !settings.show3DEffects})}
                  className={`w-16 h-8 rounded-full transition ${settings.show3DEffects ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.show3DEffects ? 'translate-x-8' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">Background Animations</p>
                  <p className="text-sm text-gray-400">Planets, comets, ʻOumuamua, supernova</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, showAnimations: !settings.showAnimations})}
                  className={`w-16 h-8 rounded-full transition ${settings.showAnimations ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.showAnimations ? 'translate-x-8' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
          )}

          {/* SECURITY TAB */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">Security Settings</h2>
              
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">Require 2FA for All Users</p>
                  <p className="text-sm text-gray-400">Enforce two-factor authentication</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, require2FA: !settings.require2FA})}
                  className={`w-16 h-8 rounded-full transition ${settings.require2FA ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.require2FA ? 'translate-x-8' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Session Timeout (days)</label>
                <input
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) => setSettings({...settings, sessionTimeout: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  min="1"
                  max="30"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Max Login Attempts</label>
                <input
                  type="number"
                  value={settings.maxLoginAttempts}
                  onChange={(e) => setSettings({...settings, maxLoginAttempts: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  min="3"
                  max="10"
                />
              </div>
            </div>
          )}

          {/* ADMIN USERS TAB */}
          {activeTab === 'admins' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">Admin User Management</h2>
              
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-6 h-6 text-yellow-400" />
                  <p className="font-bold text-yellow-300">Platform Owner</p>
                </div>
                <p className="text-white font-semibold">malcolmlee3@gmail.com</p>
                <p className="text-xs text-gray-400 mt-1">Permanent access - cannot be removed</p>
              </div>

              <div>
                <h3 className="font-bold mb-3">Add New Admin</h3>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <button
                    onClick={addAdmin}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition"
                  >
                    Add Admin
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-3">Current Admins ({adminUsers.length})</h3>
                <div className="space-y-2">
                  {adminUsers.map(admin => (
                    <div key={admin.id} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                      <div>
                        <p className="font-semibold">{admin.full_name}</p>
                        <p className="text-sm text-gray-400">{admin.email}</p>
                      </div>
                      {admin.email !== 'malcolmlee3@gmail.com' && (
                        <button className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition">
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MAINTENANCE TAB */}
          {activeTab === 'maintenance' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">Maintenance & Data Management</h2>
              
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <p className="font-semibold">Cache System</p>
                  <p className="text-sm text-gray-400">Enable caching for better performance</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, cacheEnabled: !settings.cacheEnabled})}
                  className={`w-16 h-8 rounded-full transition ${settings.cacheEnabled ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-7 h-7 bg-white rounded-full transition-transform ${settings.cacheEnabled ? 'translate-x-8' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Cache Expiry (hours)</label>
                <input
                  type="number"
                  value={settings.cacheExpiry}
                  onChange={(e) => setSettings({...settings, cacheExpiry: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={clearCache}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-orange-600 hover:bg-orange-700 rounded-lg font-semibold transition"
                >
                  <Trash2 className="w-5 h-5" />
                  Clear All Cache
                </button>
                <button
                  onClick={clearOldData}
                  className="flex items-center justify-center gap-2 px-6 py-4 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
                >
                  <Database className="w-5 h-5" />
                  Clean Old Data
                </button>
              </div>

              <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4">
                <h3 className="font-bold mb-2">💡 Maintenance Actions</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li><strong>Clear Cache</strong>: Removes browser cache, logs out users</li>
                  <li><strong>Clean Old Data</strong>: Deletes expired sessions & anonymous data</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveAdminSettings;
